<template>
  <section v-if="showWorkTime" :class="[prefixCls, showEnvWrapCls]">
    <span :class="workClass" class="title inline-flex items-center">
      <IconFont :name="iconName" class="work-icon" />
      {{ title }}
    </span>
    <span> {{ timeStart }} - {{ timeEnd }} </span>
    <SvgIcon v-if="showEnv" :name="envIcon" size="48" class="absolute top-0 right-0 -mt-1" />
  </section>
</template>

<script setup lang="ts">
  import { useDesign } from '/@/hooks/web/useDesign';
  import { useI18n, useLocale } from '@mcex/locale';
  import { IconFont, SvgIcon } from '/@/components/Icon';
  import { computed, onUnmounted, ref, unref, watchEffect } from 'vue';
  import dayjs from 'dayjs';
  import { useAppStore } from '/@/store/modules/app';
  import { fetchTransactionTime } from '/@/api/exchangeService';
  import { useDocumentVisibility } from '@vueuse/core';
  import { useRoute } from 'vue-router';
  import { useTradEtpStore } from '/@/store/modules/exchangeService/tradeEtp';
  const UAT = 'exmt-uat';
  const DEV = 'exmt-dev1';
  const STG = 'stg2';
  const { t } = useI18n();
  const { getLocale } = useLocale();
  const { prefixCls } = useDesign('work-time-box');
  const now = ref(new Date());
  const appStore = useAppStore();
  const isTradeDay = ref(false);
  const openTime = ref();
  const closeTime = ref();
  // 是否显示环境
  const showEnv = computed(() => {
    // 正则匹配域名是否为uat或者dev环境
    const reg = new RegExp(`${DEV}|${STG}|${UAT}`);
    return reg.test(window.location.host);
  });
  const envIcon = computed(() => {
    const { host } = window.location;
    const endFix = getLocale.value === 'en' ? '_en' : '';
    if (new RegExp(`${UAT}`).test(host)) {
      return 'sys-env_uat' + endFix;
    }
    if (new RegExp(`${DEV}`).test(host)) {
      return 'sys-env_dev' + endFix;
    }
    if (new RegExp(`${STG}`).test(host)) {
      return 'sys-env_stg' + endFix;
    }
    return '';
  });
  const showEnvWrapCls = computed(() => {
    return showEnv.value ? 'relative !pr-14.5' : '';
  });
  const timeStart = computed(() => {
    if (!unref(isTradeDay)) {
      return '00:00 AM';
    }
    return dayjs(`${dayjs().format('YYYY-MM-DD')} ${unref(openTime)}`).format('h:mm A');
  });
  const timeEnd = computed(() => {
    if (!unref(isTradeDay)) {
      return '00:00 PM';
    }
    return dayjs(`${dayjs().format('YYYY-MM-DD')} ${unref(closeTime)}`).format('h:mm A');
  });
  // 是否在交易时间
  const isWork = computed(() => {
    // 获取当前时间
    // 判断当前时间是否在09:30:00 - 16:30:00之间
    const isBetween = dayjs(now.value).isBetween(
      dayjs(`${dayjs().format('YYYY-MM-DD')} ${unref(openTime)}`),
      dayjs(`${dayjs().format('YYYY-MM-DD')} ${unref(closeTime)}`),
    );
    return unref(isTradeDay) && isBetween;
  });
  const iconName = computed(() => {
    return isWork.value ? 'M-terminal-icontrading' : 'M-terminal-iconrest';
  });
  const title = computed(() => {
    return isWork.value ? t('common.opening') : t('common.closing');
  });
  const workClass = computed(() => {
    return isWork.value ? 'working' : 'cafe';
  });

  let timer: Nullable<any> = null;

  const checkTimeWork = () => {
    // 定时器存在则跳过
    if (timer) return;

    // 如果离交易时间开始间隔在一分钟内，每隔一秒刷新一次
    if (dayjs().diff(dayjs(`${dayjs().format('YYYY-MM-DD')} ${unref(openTime)}`), 'minute') < 1) {
      timer = setInterval(() => {
        now.value = new Date();
        // 过了交易开始时间后，清除定时器
        if (
          dayjs().isAfter(dayjs(`${dayjs().format('YYYY-MM-DD')} ${unref(openTime)}`), 'second')
        ) {
          timer && clearInterval(timer);
          timer = null;
        }
      }, 1000);
      return;
    }

    // 如果离交易时间结束间隔在一分钟内，每隔一秒刷新一次
    if (dayjs().diff(dayjs(`${dayjs().format('YYYY-MM-DD')} ${unref(closeTime)}`), 'minute') < 1) {
      timer = setInterval(() => {
        now.value = new Date();
        // 过了交易结束时间后，清除定时器
        if (
          dayjs().isAfter(dayjs(`${dayjs().format('YYYY-MM-DD')} ${unref(closeTime)}`), 'second')
        ) {
          timer && clearInterval(timer);
          timer = null;
        }
      }, 1000);
      return;
    }

    // 如果在交易时间内且isWork为false，则重置now.value = new Date();重新计算isWork
    const isBetween = dayjs(new Date()).isBetween(
      dayjs(`${dayjs().format('YYYY-MM-DD')} ${unref(openTime)}`),
      dayjs(`${dayjs().format('YYYY-MM-DD')} ${unref(closeTime)}`),
    );

    // 在交易时间内且isWork为false或者不在交易时间内且isWork为true
    if ((isBetween && !isWork.value) || (!isBetween && isWork.value)) {
      now.value = new Date();
      return;
    }
  };

  // 路由变化后，判断是否在交易时间
  watchEffect(() => {
    appStore.checkTime;
    checkTimeWork();
  });
  onUnmounted(() => {
    timer && clearInterval(timer);
    timer = null;
  });
  const tradDrpStore = useTradEtpStore();
  const updateTime = async () => {
    const { isTradeDay: tradeDay, tradeTimeSpan, feeRateChanged } = await fetchTransactionTime();
    // TODO:下个版本移除 获取最新费率
    tradDrpStore.feeRateChanged = feeRateChanged;

    //截取交易时间
    const [openT, closeT] = tradeTimeSpan.split('-');
    openTime.value = openT;
    closeTime.value = closeT;
    isTradeDay.value = tradeDay;
  };
  /**
   * 窗口聚焦时，判断是否在交易时间
   */
  const visibility = useDocumentVisibility();
  watchEffect(() => {
    if (visibility.value === 'visible') {
      updateTime();
    }
  });

  const currentRoute = useRoute();
  const showWorkTime = computed(() => {
    const toPackage = currentRoute.matched[0]?.meta?.package;
    if (toPackage) {
      return false;
    }
    return true;
  });
</script>

<style lang="less" scoped>
  @prefix-cls: ~'@{namespace}-work-time-box';

  .@{prefix-cls} {
    color: #a2abbe;
    background-color: #20252d;
    padding: 2px 8px;
    border-radius: @first-card-radius;
    display: flex;
    .text-xs(600);

    .work-icon {
      font-size: 12px;
      margin-right: 4px;
    }

    .title {
      margin-right: 10px;

      &.working {
        color: #2cbc98;
      }

      &.cafe {
        color: @emphasis-text;
      }
    }
  }
</style>
